import React, { useState, useContext } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Upload, X } from 'lucide-react';
import AuthBrandPanel from '../components/AuthBrandPanel';
import './AuthPages.css';
import NotificationModal from '../components/NotificationModal';
import { useNotification } from '../hooks/useNotification';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { validatePassword, isCommonPassword } from '../utils/passwordValidation';

const Register = () => {
  const [searchParams] = useSearchParams();
  const referral = searchParams.get('referral');
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    name: '', 
    role: 'client', 
    registration_number: '',
    specialization: '',
    experience_years: '',
    qualifications: '',
    bio: '',
    photo_url: '',
    consent_accepted: false
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const { notification, showSuccess, showError, showInfo, closeNotification } = useNotification();

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate consent
    if (!formData.consent_accepted) {
      showError('You must accept the Privacy Policy and Terms of Service to continue');
      setLoading(false);
      return;
    }
    
    // Validate password strength
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      showError(`Password requirements not met: ${passwordValidation.errors.join(', ')}`);
      setLoading(false);
      return;
    }
    
    // Check for common passwords
    if (isCommonPassword(formData.password)) {
      showError('This password is too common. Please choose a more secure password.');
      setLoading(false);
      return;
    }

    try {
      let res;
      
      // If psychologist with photo, use multipart endpoint
      if (formData.role === 'psychologist' && profilePhoto) {
        const formDataToSend = new FormData();
        formDataToSend.append('email', formData.email);
        formDataToSend.append('password', formData.password);
        formDataToSend.append('name', formData.name);
        formDataToSend.append('role', formData.role);
        formDataToSend.append('registration_number', formData.registration_number);
        formDataToSend.append('consent_accepted', formData.consent_accepted);
        if (formData.specialization) formDataToSend.append('specialization', formData.specialization);
        if (formData.experience_years) formDataToSend.append('experience_years', formData.experience_years);
        if (formData.qualifications) formDataToSend.append('qualifications', formData.qualifications);
        if (formData.bio) formDataToSend.append('bio', formData.bio);
        formDataToSend.append('photo', profilePhoto);
        
        res = await axios.post(`${API}/auth/register-with-photo`, formDataToSend);
      } else {
        // Use regular JSON endpoint for clients or psychologists without photo
        const cleanedFormData = { ...formData };
        
        // Remove psychologist-specific fields if role is client or if they're empty
        if (formData.role === 'client') {
          delete cleanedFormData.specialization;
          delete cleanedFormData.experience_years;
          delete cleanedFormData.qualifications;
          delete cleanedFormData.bio;
          delete cleanedFormData.photo_url;
          delete cleanedFormData.registration_number;
        } else {
          // For psychologists, convert empty strings to null for integer fields
          if (cleanedFormData.experience_years === '') {
            cleanedFormData.experience_years = null;
          }
        }
        
        res = await axios.post(`${API}/auth/register`, { ...cleanedFormData, ...(referral ? { role: "client", referral_token: referral } : {}) });
      }
      
      login(res.data.access_token, res.data.user);
      
      if (res.data.user.role === 'psychologist') {
        showInfo('Your account is pending approval. You will be notified once approved by admin.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        showSuccess('Account created successfully!');
        setTimeout(() => {
          navigate(`/${res.data.user.role}-dashboard`);
        }, 1500);
      }
    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // If detail is an array (FastAPI validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
        // If detail is a string
        else {
          errorMessage = detail;
        }
      }
      
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="insight-auth insight-auth--register">
      <AuthBrandPanel />
      <main className="insight-auth-main">
      <div className="insight-auth-form" data-testid="register-form">
        <div className="mb-6">
          <Link to="/" className="insight-auth-home">
            ← Back to Home
          </Link>
        </div>
        <header className="insight-auth-heading"><h1>Create Your Professional Account</h1><p>Register to access INSIGHT's structured qualitative exploration platform.</p></header>
        
        <form onSubmit={handleSubmit} className="insight-auth-fields">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              required
              data-testid="register-name-input"
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              required
              data-testid="register-email-input"
            />
          </div>
          <div className="insight-auth-wide">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              required
              data-testid="register-password-input"
            />
            <PasswordStrengthIndicator 
              password={formData.password}
              requirements={validatePassword(formData.password)}
            />
          </div>
          <div className="insight-auth-wide">
            <Label htmlFor="role">I am a</Label>
            <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
              <SelectTrigger id="role" disabled={!!referral} data-testid="register-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="client" data-testid="role-client">Client</SelectItem>
                <SelectItem value="psychologist" data-testid="role-psychologist">Psychologist</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Psychologist proforma fields */}
          {formData.role === 'psychologist' && (
            <>
              <h2 className="insight-auth-section">Professional details</h2>
              <div className="insight-auth-wide">
                <Label htmlFor="registration_number">RCI CRR NO / Registration Number *</Label>
                <Input
                  id="registration_number"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  placeholder="Enter your registration number"
                  required
                  data-testid="register-registration-number-input"
                />
              </div>
              
              <div>
                <Label htmlFor="specialization">Specialization</Label>
                <Input
                  id="specialization"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  placeholder="e.g., Clinical Psychology, Child Psychology"
                  data-testid="register-specialization-input"
                />
              </div>
              
              <div>
                <Label htmlFor="experience_years">Years of Experience</Label>
                <Input
                  id="experience_years"
                  type="number"
                  min="0"
                  value={formData.experience_years}
                  onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                  placeholder="e.g., 5"
                  data-testid="register-experience-input"
                />
              </div>
              
              <div>
                <Label htmlFor="qualifications">Qualifications</Label>
                <Input
                  id="qualifications"
                  value={formData.qualifications}
                  onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
                  placeholder="e.g., M.Phil, Ph.D"
                  data-testid="register-qualifications-input"
                />
              </div>
              
              <div className="insight-auth-wide">
                <Label htmlFor="bio">Bio (Brief introduction)</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Brief description about yourself and your approach"
                  rows={3}
                  data-testid="register-bio-input"
                />
              </div>
              
              <div className="insight-auth-wide">
                <Label htmlFor="profile-photo">Profile Photo (Optional)</Label>
                <div className="space-y-3">
                  {profilePhotoPreview ? (
                    <div className="relative inline-block">
                      <img 
                        src={profilePhotoPreview} 
                        alt="Profile preview" 
                        className="w-32 h-32 rounded-full object-cover border-2 border-blue-200"
                      />
                      <button
                        type="button"
                        aria-label="Remove profile photo"
                        onClick={handleRemovePhoto}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="insight-auth-upload">
                      <label 
                        htmlFor="profile-photo"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        <span className="text-sm">Upload Photo</span>
                      </label>
                      <Input
                        id="profile-photo"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="insight-auth-file"
                        data-testid="register-photo-input"
                      />
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Upload a professional photo to help clients connect with you
                  </p>
                </div>
              </div>
              
              <p className="insight-auth-approval insight-auth-wide">
                Your account will be pending approval until verified by admin
              </p>
            </>
          )}
          
          {/* Privacy Policy & Terms Consent Checkbox */}
          <div className="insight-auth-consent insight-auth-wide flex items-start gap-3">
            <input
              type="checkbox"
              id="consent"
              checked={formData.consent_accepted}
              onChange={(e) => setFormData({ ...formData, consent_accepted: e.target.checked })}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              data-testid="consent-checkbox"
            />
            <label htmlFor="consent" className="text-sm text-gray-700 leading-relaxed">
              I agree to the{' '}
              <Link to="/privacy-policy" target="_blank" className="text-blue-600 hover:underline font-semibold">
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link to="/terms-of-service" target="_blank" className="text-blue-600 hover:underline font-semibold">
                Terms of Service
              </Link>
              {' '}of INSIGHT platform. I understand that my data will be processed as per these policies.
            </label>
          </div>
          
          <Button type="submit" className="insight-auth-wide w-full" disabled={loading || !formData.consent_accepted} data-testid="register-submit-btn">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</> : 'Create Account'}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-gray-600">
          Already have an account?{' '}
          <Link to={referral ? `/login?referral=${encodeURIComponent(referral)}` : "/login"} className="font-semibold" style={{ color: '#117F85' }} data-testid="register-login-link">
            Sign in
          </Link>
        </p>
      </div>
      </main>
      
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  );
};

export default Register;